import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  ArrowLeft, Loader2, CalendarDays, FileText, ImageIcon, Download, Share2, Send,
  Plus, Trash2, Copy,
} from "lucide-react";
import TestTimetablePoster from "@/components/announcement/TestTimetablePoster";
import { exportImage, exportPdf, exportDataUrl, shareNode, copyNodeImage } from "@/components/announcement/exportUtils";
import TemplateBar from "@/components/announcement/TemplateBar";

const DAY_ABBR = ["SUN", "MON", "TUES", "WED", "THU", "FRI", "SAT"];

// Compute a DD/MM/YYYY string + weekday abbreviation from a yyyy-mm-dd input.
function isoToPretty(iso) {
  if (!iso) return { date: "", day: "" };
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return { date: "", day: "" };
  const dt = new Date(Date.UTC(y, m - 1, d));
  const date = `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  return { date, day: DAY_ABBR[dt.getUTCDay()] };
}

const TEST_PRESETS = [
  "PERIODIC TEST - 1", "PERIODIC TEST - 2", "PERIODIC TEST - 3",
  "FORMATIVE ASSESSMENT - 1", "FORMATIVE ASSESSMENT - 2",
  "MID TERM EXAMINATION", "FINAL EXAMINATION",
  "UNIT TEST",
];

function emptySession() {
  return { session: "MORNING", subject: "", portions: "" };
}
function emptyRow() {
  return { iso: "", date: "", day: "", sessions: [emptySession()] };
}

export default function TestTimetableGenerator({ onBack }) {
  const [options, setOptions] = useState(null);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);

  // Poster inputs
  const [className, setClassName] = useState("");
  const [sectionsText, setSectionsText] = useState("");   // e.g. "A&B", "A"
  const [testName, setTestName] = useState("PERIODIC TEST - 1");
  const [testMonthYear, setTestMonthYear] = useState(() => {
    const d = new Date();
    return `${d.toLocaleString("en-US", { month: "short" }).toUpperCase()} - ${d.getFullYear()}`;
  });
  const [rows, setRows] = useState([emptyRow()]);
  const [footerNote, setFooterNote] = useState("");

  const [exporting, setExporting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [audienceScope, setAudienceScope] = useState("section"); // section | school

  const posterRef = useRef(null);
  const previewWrapRef = useRef(null);
  const [pscale, setPscale] = useState(1);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/notices/toppers/options"),
      api.get("/school/profile").catch(() => ({ data: { profile: {} } })),
    ])
      .then(([{ data }, { data: sp }]) => {
        setOptions(data);
        setProfile((sp && sp.profile) || {});
      })
      .catch(() => toast.error("Could not load school profile."))
      .finally(() => setLoading(false));
  }, []);

  // Fit-to-width preview.
  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;
    const update = () => setPscale(Math.min(1, (el.clientWidth - 4) / 1123));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const classes = useMemo(() => {
    const seen = new Set(); const out = [];
    (options?.sections || []).forEach((s) => { if (!seen.has(s.class_name)) { seen.add(s.class_name); out.push(s.class_name); } });
    return out;
  }, [options]);
  const sectionsForClass = useMemo(
    () => [...new Set((options?.sections || []).filter((s) => s.class_name === className).map((s) => s.section))],
    [options, className]
  );

  // Auto-fill "A", "A&B", "A, B, C" defaults for the class when it changes.
  useEffect(() => {
    if (!className) { setSectionsText(""); return; }
    if (sectionsForClass.length === 0) setSectionsText("");
    else if (sectionsForClass.length === 1) setSectionsText(sectionsForClass[0]);
    else setSectionsText(sectionsForClass.slice().sort().join("&"));
  }, [className, sectionsForClass]);

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const setRowDate = (i, iso) => {
    const { date, day } = isoToPretty(iso);
    updateRow(i, { iso, date, day });
  };
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const dupRow = (i) => setRows((rs) => {
    const copy = JSON.parse(JSON.stringify(rs[i] || emptyRow()));
    return [...rs.slice(0, i + 1), copy, ...rs.slice(i + 1)];
  });
  const delRow = (i) => setRows((rs) => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);

  const addSession = (ri) => setRows((rs) => rs.map((r, idx) => idx === ri ? { ...r, sessions: [...r.sessions, emptySession()] } : r));
  const delSession = (ri, si) => setRows((rs) => rs.map((r, idx) => idx === ri ? { ...r, sessions: r.sessions.length > 1 ? r.sessions.filter((_, j) => j !== si) : r.sessions } : r));
  const updateSession = (ri, si, patch) => setRows((rs) => rs.map((r, idx) => idx === ri ? {
    ...r, sessions: r.sessions.map((s, j) => j === si ? { ...s, ...patch } : s),
  } : r));

  const posterData = {
    profile,
    class_name: className,
    sections: sectionsText,
    test_name: testName,
    test_month_year: testMonthYear,
    rows,
    footer_note: footerNote,
  };

  const posterTitle = `TestTimetable-${(testName || "test").replace(/[^\w-]+/g, "_")}-${className || "class"}-${sectionsText || ""}`;

  const doExport = async (kind) => {
    if (!posterRef.current) return;
    setExporting(true);
    try {
      if (kind === "pdf") await exportPdf(posterRef.current, posterTitle, "landscape");
      else await exportImage(posterRef.current, posterTitle, kind);
    } catch { toast.error("Export failed. Please try again."); }
    finally { setExporting(false); }
  };

  const doShare = async () => {
    if (!posterRef.current) return;
    setExporting(true);
    try {
      const res = await shareNode(posterRef.current, posterTitle);
      if (res === "unsupported") { await doExport("png"); toast.message("Sharing isn't available here — the image was downloaded so you can share it manually."); }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share."); }
    finally { setExporting(false); }
  };

  const doCopy = async () => {
    if (!posterRef.current) return;
    setExporting(true);
    try {
      const res = await copyNodeImage(posterRef.current);
      if (res === "copied") toast.success("Poster image copied to clipboard.");
      else { await doExport("png"); toast.message("Clipboard not available — downloaded the image instead."); }
    } catch { toast.error("Could not copy image."); }
    finally { setExporting(false); }
  };

  const doPublish = async () => {
    if (!posterRef.current) return;
    if (!className) { toast.error("Pick a class first."); return; }
    if (!rows.some((r) => r.date)) { toast.error("Add at least one test date."); return; }
    setPublishing(true);
    try {
      const image = await exportDataUrl(posterRef.current, "jpeg", 2.5);
      // Split "A&B", "A, B" or "A B" into individual section codes to build the audience.
      const secs = (sectionsText || "").split(/[&,\s]+/).map((s) => s.trim()).filter(Boolean);
      const audience = audienceScope === "school" || secs.length === 0
        ? { type: "school", recipients: ["students", "parents", "teachers"] }
        : {
            type: "sections",
            sections: secs.map((s) => ({ class_name: String(className), section: s })),
            recipients: ["students", "parents", "teachers"],
          };
      await api.post("/notices?publish=true", {
        title: `${testName} — Class ${className}${sectionsText ? ` (${sectionsText})` : ""} Timetable`,
        category: "Examination",
        description: `<p>${testName}${testMonthYear ? ` (${testMonthYear})` : ""} — timetable with portions for Class ${className}${sectionsText ? ` (${sectionsText})` : ""}. See attached poster.</p>`,
        priority: "important",
        image,
        announcement_date: new Date().toISOString().slice(0, 10),
        audience,
      });
      toast.success("Test timetable announcement published!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish. Ensure the school profile is complete.");
    } finally { setPublishing(false); }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-slate-400" data-testid="test-timetable-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="test-timetable-generator">
      <div className="flex items-center gap-3 flex-wrap">
        {onBack && (
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onBack} data-testid="test-timetable-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        )}
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <CalendarDays className="h-5 w-5 text-violet-500" /> Test Timetable with Portions
          </div>
          <p className="text-sm text-slate-500">Build a ready-to-share timetable poster for any class and publish it as an announcement.</p>
        </div>
        <TemplateBar
          kind="test-timetable"
          testIdPrefix="tt"
          getState={() => ({
            className, sectionsText, testName, testMonthYear, rows, footerNote, audienceScope,
          })}
          applyState={(s) => {
            if (!s || typeof s !== "object") return;
            if (typeof s.className === "string") setClassName(s.className);
            if (typeof s.sectionsText === "string") setSectionsText(s.sectionsText);
            if (typeof s.testName === "string") setTestName(s.testName);
            if (typeof s.testMonthYear === "string") setTestMonthYear(s.testMonthYear);
            if (Array.isArray(s.rows) && s.rows.length) setRows(s.rows);
            if (typeof s.footerNote === "string") setFooterNote(s.footerNote);
            if (s.audienceScope === "section" || s.audienceScope === "school") setAudienceScope(s.audienceScope);
          }}
        />
      </div>

      <div className="grid lg:grid-cols-[420px_1fr] gap-5">
        {/* ── Editor ── */}
        <div className="space-y-3">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Class</Label>
                <Select value={className} onValueChange={setClassName}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="tt-class"><SelectValue placeholder="Pick class…" /></SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => (
                      <SelectItem key={c} value={c}>
                        {["Play Group", "LKG", "UKG"].includes(c) ? c : `Class ${c}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Section(s)</Label>
                <Input
                  value={sectionsText}
                  onChange={(e) => setSectionsText(e.target.value)}
                  placeholder="e.g. A&B"
                  className="rounded-xl mt-1"
                  data-testid="tt-sections"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Test Name</Label>
              <Input value={testName} onChange={(e) => setTestName(e.target.value)} className="rounded-xl mt-1" data-testid="tt-test-name" />
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {TEST_PRESETS.map((t) => (
                  <button key={t} onClick={() => setTestName(t)} className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid={`tt-preset-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{t}</button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Month · Year</Label>
              <Input value={testMonthYear} onChange={(e) => setTestMonthYear(e.target.value)} placeholder="Sep - 2026" className="rounded-xl mt-1" data-testid="tt-month-year" />
            </div>
          </div>

          {/* Rows editor */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Test Dates & Sessions</Label>
              <Button size="sm" variant="outline" className="rounded-full" onClick={addRow} data-testid="tt-add-date">
                <Plus className="h-4 w-4 mr-1" /> Add date
              </Button>
            </div>

            <div className="space-y-3">
              {rows.map((r, ri) => (
                <div key={ri} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid={`tt-row-${ri}`}>
                  <div className="grid grid-cols-[1fr_60px_28px_28px] gap-2 items-end">
                    <div>
                      <Label className="text-[11px] text-slate-500">Date</Label>
                      <Input type="date" value={r.iso} onChange={(e) => setRowDate(ri, e.target.value)} className="rounded-lg mt-0.5 h-9" data-testid={`tt-row-date-${ri}`} />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Day</Label>
                      <Input value={r.day} onChange={(e) => updateRow(ri, { day: e.target.value })} className="rounded-lg mt-0.5 h-9 text-center" data-testid={`tt-row-day-${ri}`} />
                    </div>
                    <button onClick={() => dupRow(ri)} className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center text-slate-500" title="Duplicate row" data-testid={`tt-row-dup-${ri}`}>
                      <Copy className="h-4 w-4" />
                    </button>
                    <button onClick={() => delRow(ri)} disabled={rows.length === 1} className="h-9 rounded-lg border border-rose-200 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center justify-center disabled:opacity-40" title="Remove row" data-testid={`tt-row-del-${ri}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-2 pt-1">
                    {r.sessions.map((s, si) => (
                      <div key={si} className="grid grid-cols-[110px_100px_1fr_28px] gap-2 items-center" data-testid={`tt-row-${ri}-session-${si}`}>
                        <Input value={s.session} onChange={(e) => updateSession(ri, si, { session: e.target.value })} placeholder="Morning" className="rounded-lg h-9 text-xs" data-testid={`tt-session-${ri}-${si}`} />
                        <Input value={s.subject} onChange={(e) => updateSession(ri, si, { subject: e.target.value })} placeholder="Subject" className="rounded-lg h-9 text-xs text-center" data-testid={`tt-subject-${ri}-${si}`} />
                        <Textarea value={s.portions} onChange={(e) => updateSession(ri, si, { portions: e.target.value })} placeholder="Chapter / portions / references" rows={2} className="rounded-lg text-xs min-h-[36px]" data-testid={`tt-portions-${ri}-${si}`} />
                        <button onClick={() => delSession(ri, si)} disabled={r.sessions.length === 1} className="h-9 rounded-lg border border-rose-200 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center justify-center disabled:opacity-40" title="Remove session" data-testid={`tt-session-del-${ri}-${si}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <Button size="sm" variant="ghost" className="rounded-full text-xs h-7" onClick={() => addSession(ri)} data-testid={`tt-add-session-${ri}`}>
                      <Plus className="h-3 w-3 mr-1" /> Add session
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Footer note (optional)</Label>
            <Textarea value={footerNote} onChange={(e) => setFooterNote(e.target.value)} rows={3} placeholder="e.g. ICT PRACTICAL: WORKING WITH GCOMPRIS…" className="rounded-xl" data-testid="tt-footer" />
          </div>

          {/* Actions */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Publish to</Label>
              <Select value={audienceScope} onValueChange={setAudienceScope}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="tt-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="section">This class{sectionsText ? ` — sections ${sectionsText}` : ""}</SelectItem>
                  <SelectItem value="school">Entire school</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => doExport("pdf")} disabled={exporting} data-testid="tt-download-pdf">
                {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />} PDF
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => doExport("png")} disabled={exporting} data-testid="tt-download-png">
                <ImageIcon className="h-4 w-4 mr-1" /> PNG
              </Button>
              <Button variant="outline" className="rounded-full" onClick={doCopy} disabled={exporting} data-testid="tt-copy">
                <Copy className="h-4 w-4 mr-1" /> Copy image
              </Button>
              <Button variant="outline" className="rounded-full" onClick={doShare} disabled={exporting} data-testid="tt-share">
                <Share2 className="h-4 w-4 mr-1" /> Share
              </Button>
            </div>
            <Button className="rounded-full w-full" onClick={doPublish} disabled={publishing} data-testid="tt-publish">
              {publishing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Publish as Announcement
            </Button>
          </div>
        </div>

        {/* ── Preview ── */}
        <div>
          <div ref={previewWrapRef} className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/40 p-3 overflow-hidden" data-testid="tt-preview-wrap">
            <div style={{ height: 794 * pscale, width: "100%", position: "relative" }}>
              <div style={{ width: 1123, height: 794, transform: `scale(${pscale})`, transformOrigin: "top left" }}>
                <TestTimetablePoster ref={posterRef} data={posterData} />
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Live preview scales to fit — the download stays crisp at full A4 landscape.</p>
        </div>
      </div>
    </div>
  );
}
